import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";
import {
  AlertTriangle,
  ArrowRight,
  Check,
  ChevronLeft,
  ChevronRight,
  CreditCard,
  ExternalLink,
  Heart,
  Loader2,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { StatusBadge } from "@/components/shared/StatusBadge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import { LoadingPage } from "@/components/shared/LoadingPage";
import { PageHeader } from "@/components/shared/PageHeader";
import {
  describeTenantRegime,
  type TenantRegimeKind,
  REGIME_LABEL,
  LEGAL_FORM_LABEL,
  resolveTenantLegalForm,
} from "@/lib/tenantRegime";
import { BoletoPagoCard } from "@/components/features/accountant/BoletoPagoCard";
import {
  AccountantFiscalBenefitCard,
  isFiscalBenefitDeclarationPending,
} from "@/components/features/accountant/AccountantFiscalBenefitCard";
import { AccountantFiscalProfileCard } from "@/components/features/accountant/AccountantFiscalProfileCard";
import { SettingsFiscalIdentityCard } from "@/pages/settings/SettingsFiscalIdentityCard";
import {
  getAccountantTenantFiscalIdentity,
  getAccountantTenantFiscalIdentityFromDgi,
  updateAccountantTenantFiscalIdentity,
} from "@/services/fiscalIdentityService";
import { AccountantBajaCard } from "@/components/features/accountant/AccountantBajaCard";
import { AccountantObligationPaidControl } from "@/components/features/accountant/AccountantObligationPaidControl";
import { AccountantHealthAlerts } from "@/components/features/accountant/AccountantHealthAlerts";
import { GeneratePaymentLinkDialog } from "@/components/features/accountant/GeneratePaymentLinkDialog";
import { CierreFiscalTab } from "@/components/features/accountant/cierre/CierreFiscalTab";
import { useTenantSwitcher } from "@/contexts/TenantSwitcherContext";
import { cn } from "@/lib/utils";
import { formatDashboardCurrency } from "@/lib/currencyFormat";
import { formatDisplayDate } from "@/lib/dateFormat";
import {
  getAccountantSubdiarioSummary,
  getAccountantTenantDetail,
  getAccountantTenantIvaHistory,
} from "@/services/dashboardService";
import { getFiscalBenefitDeclarationForTenant } from "@/services/fiscalService";

function formatUyu(amount: number, locale: string): string {
  return `UYU ${amount.toLocaleString(locale === "es" ? "es-UY" : "en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

const MONTH_LABELS_ES = [
  "Ene",
  "Feb",
  "Mar",
  "Abr",
  "May",
  "Jun",
  "Jul",
  "Ago",
  "Sep",
  "Oct",
  "Nov",
  "Dic",
];

function formatMonth(ym: string): string {
  const [year, month] = ym.split("-");
  const idx = Number(month) - 1;
  return `${MONTH_LABELS_ES[idx] ?? month} ${(year ?? "").slice(2)}`;
}

const REGIME_LABEL_ES: Record<string, string> = {
  standard: "Régimen general",
  minimum: "Literal E",
  "minimum-with-benefit": "Literal E con beneficio",
};

/** Human label for the IVA regime / DGI classification. Known regimes map to
 *  Spanish copy; anything else (e.g. "no_cede" → "NO CEDE") is de-snake-cased
 *  and uppercased so raw DB values never reach the UI. */
function formatRegimeLabel(regime: string): string {
  return REGIME_LABEL_ES[regime] ?? regime.replace(/_/g, " ").toUpperCase();
}

const TAB_RESUMEN = "resumen";
const TAB_IVA = "iva";
const TAB_PERFIL = "perfil";
const TAB_CIERRE = "cierre";
const DETAIL_TABS = [TAB_RESUMEN, TAB_IVA, TAB_PERFIL, TAB_CIERRE] as const;

export function AccountantClientDetailPage(): JSX.Element {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { tenantId } = useParams<{ tenantId: string }>();
  const { switchTenant, isSwitchingTenant } = useTenantSwitcher();
  const locale = i18n.language;
  const now = new Date();
  const currentYm = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get("tab");
  const activeTab = DETAIL_TABS.includes(tabParam as (typeof DETAIL_TABS)[number])
    ? (tabParam as string)
    : TAB_RESUMEN;
  const setTab = (value: string): void => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (value === TAB_RESUMEN) next.delete("tab");
        else next.set("tab", value);
        return next;
      },
      { replace: true },
    );
  };

  const detailQuery = useQuery({
    queryKey: ["accountant", "tenant-detail", tenantId ?? ""],
    queryFn: () => getAccountantTenantDetail(tenantId!),
    enabled: Boolean(tenantId),
    staleTime: 30_000,
  });

  const ivaHistoryQuery = useQuery({
    queryKey: ["accountant", "tenant-iva-history", tenantId ?? ""],
    queryFn: () => getAccountantTenantIvaHistory(tenantId!, 12),
    enabled: Boolean(tenantId),
    staleTime: 60_000,
  });

  const subdiarioQuery = useQuery({
    queryKey: ["accountant", "tenant-subdiario-summary", tenantId ?? ""],
    queryFn: () => getAccountantSubdiarioSummary(tenantId!),
    enabled: Boolean(tenantId),
    staleTime: 60_000,
  });

  /**
   * Drives the attention dot on the Perfil fiscal tab.
   *
   * Same queryKey as AccountantFiscalBenefitCard, so the two share one cache
   * entry: declaring from that card invalidates this key and the dot clears
   * without a second round-trip. Must sit above the early returns below —
   * hooks cannot run conditionally.
   */
  const benefitDeclarationQuery = useQuery({
    queryKey: ["accountant", "fiscal-benefit-declaration", tenantId ?? ""],
    queryFn: () => getFiscalBenefitDeclarationForTenant(tenantId!),
    enabled: Boolean(tenantId),
    retry: false,
  });
  const perfilNeedsAttention =
    benefitDeclarationQuery.isSuccess &&
    isFiscalBenefitDeclarationPending(benefitDeclarationQuery.data);

  const detail = detailQuery.data;

  const [showPaymentLink, setShowPaymentLink] = useState(false);

  // Auto-redirect to onboarding when the tenant hasn't finished setup —
  // matches the original cartera click behavior so accountants don't
  // get stranded on an empty detail page.
  useEffect(() => {
    if (detail?.tenant.onboardingPending && tenantId) {
      void switchTenant(tenantId).then(() => {
        navigate(`/accountant/client/${tenantId}/onboarding`, {
          replace: true,
        });
      });
    }
  }, [detail?.tenant.onboardingPending, navigate, switchTenant, tenantId]);

  const handleImpersonate = async (): Promise<void> => {
    if (!tenantId) return;
    try {
      await switchTenant(tenantId);
      navigate("/dashboard");
    } catch (err) {
      toast.error(
        err instanceof Error
          ? err.message
          : t(
              "accountantClientDetail.impersonateError",
              "No pudimos cambiar de cuenta. Probá de nuevo.",
            ),
      );
    }
  };

  if (!tenantId) {
    return (
      <div className="fx-content">
        <PageHeader
          title={t(
            "accountantClientDetail.invalidId",
            "Cliente inválido",
          )}
          onBack={() => navigate("/accountant")}
        />
      </div>
    );
  }

  if (detailQuery.isLoading) return <LoadingPage />;

  if (detailQuery.isError || !detail) {
    return (
      <div className="fx-content space-y-4">
        <PageHeader
          title={t("accountantClientDetail.title", "Cliente")}
          onBack={() => navigate("/accountant")}
        />
        <p className="text-sm text-muted-foreground">
          {t(
            "accountantClientDetail.notFound",
            "No pudimos cargar este cliente. Probá de nuevo.",
          )}
        </p>
      </div>
    );
  }

  // Don't render the page when redirecting to onboarding — avoid the
  // flash of "Aún en onboarding" content.
  if (detail.tenant.onboardingPending) {
    return <LoadingPage />;
  }

  const {
    tenant,
    health,
    ivaCurrent,
    upcomingObligations,
    pendingAck,
    rejections,
  } = detail;
  // Includes the pending declaration and open rechazos even though neither
  // costs points here: calculateFiscalHealthScore only measures DGI
  // obligations, so the score legitimately stays 100 — but "Cliente al día —
  // sin deducciones activas" sitting directly above a "falta la declaración"
  // or "rechazado por DGI" alert reads as a bug.
  const hasHealthIssues =
    pendingAck.count > 0 ||
    perfilNeedsAttention ||
    rejections.length > 0 ||
    health.deductions.some((d) => d.points < 0 || d.code === "missing_bps");
  // Legal form and fiscal regime are DIFFERENT things and an accountant needs
  // both at a glance: "SAS" says how the company is constituted, "Literal E"
  // says what it files under. Monotributo is the one value that is both, so it
  // is shown once rather than twice.
  const regime = describeTenantRegime(tenant);
  // Shared with settings so the two surfaces cannot drift on what counts as a
  // legal form — notably that `monotributo` on company.companyType is not one.
  const legalForm = resolveTenantLegalForm(null, tenant.companyType);
  const legalFormLabel = legalForm
    ? t(LEGAL_FORM_LABEL[legalForm][0], LEGAL_FORM_LABEL[legalForm][1])
    : null;
  const regimeLabel: Record<TenantRegimeKind, string | null> = {
    monotributo: t(...REGIME_LABEL.monotributo),
    "literal-e": t(...REGIME_LABEL["literal-e"]),
    cede: t(...REGIME_LABEL.cede),
    "no-cede": t(...REGIME_LABEL["no-cede"]),
    "servicios-personales": t(...REGIME_LABEL["servicios-personales"]),
    // Not a regime — the fiscal profile has not been derived yet, so no badge
    // rather than a badge saying nothing. Local, not shared: the cartera table
    // shows a dash here and settings says "no proporcionado".
    unknown: null,
  };
  const headerBadges = [
    regime === "monotributo" ? null : legalFormLabel,
    regimeLabel[regime],
  ].filter((label): label is string => Boolean(label));

  const headerName = tenant.legalName ?? tenant.name ?? tenant.rut ?? "—";
  const headerInitials = headerName
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0] ?? "")
    .join("")
    .toUpperCase();
  // The cartera row this page opens from shows the client's mark; arriving
  // here and losing it made the two screens feel like different clients.
  // Circular, matching the cartera tile and the topbar avatar.
  const headerTitle = (
    <span className="flex flex-wrap items-center gap-x-3 gap-y-2">
      <span className="grid h-11 w-11 shrink-0 place-items-center overflow-hidden rounded-full border border-[var(--fx-s-hairline)] bg-white">
        {tenant.profileImage ? (
          <img
            src={tenant.profileImage}
            alt=""
            className="h-full w-full object-cover"
          />
        ) : (
          <span
            className="font-mono text-sm font-bold"
            // Literal, not an ink token: this tile is light in BOTH themes and
            // the dark theme re-points the ink scale to cream.
            style={{ color: "#3f3934" }}
            aria-hidden
          >
            {headerInitials}
          </span>
        )}
      </span>
      <span className="min-w-0">{headerName}</span>
      {headerBadges.map((label) => (
        <StatusBadge key={label} variant="neutral">
          {label}
        </StatusBadge>
      ))}
    </span>
  );
  const headerSubtitle = [tenant.rut ? `RUT ${tenant.rut}` : null, tenant.legalName && tenant.name && tenant.legalName !== tenant.name ? tenant.name : null]
    .filter(Boolean)
    .join(" · ");

  return (
    <div className="space-y-4 fx-content">
      <PageHeader
        title={headerTitle}
        description={headerSubtitle || undefined}
        onBack={() => navigate("/accountant")}
        actions={
          <Button
            onClick={() => void handleImpersonate()}
            disabled={isSwitchingTenant}
          >
            {isSwitchingTenant ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <ExternalLink className="mr-2 h-4 w-4" />
            )}
            {t(
              "accountantClientDetail.impersonate",
              "Acceder como cliente",
            )}
          </Button>
        }
      />

      <Tabs value={activeTab} onValueChange={setTab} className="w-full">
        <TabsList className="mb-4 gap-2">
          <TabsTrigger value={TAB_RESUMEN}>
            {t("accountantClientDetail.tabs.resumen", "Resumen")}
          </TabsTrigger>
          <TabsTrigger value={TAB_IVA}>
            {t("accountantClientDetail.tabs.iva", "IVA y Ventas")}
          </TabsTrigger>
          <TabsTrigger value={TAB_PERFIL}>
            {t("accountantClientDetail.tabs.perfil", "Perfil fiscal")}
            {/* A tab holding something the accountant owes an action on says
                so on the tab itself. The cartera row already flags "Falta
                declaración 206/019", but the client page gave no hint that
                the fix lived one tab away — the warning and the action were
                on different screens. */}
            {perfilNeedsAttention ? (
              <span
                className="ml-1.5 inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-[var(--fx-warn-text,#b45309)] align-middle"
                aria-hidden
              />
            ) : null}
            {perfilNeedsAttention ? (
              <span className="sr-only">
                {t(
                  "accountantClientDetail.tabs.needsAttention",
                  "(requiere atención)",
                )}
              </span>
            ) : null}
          </TabsTrigger>
          <TabsTrigger value={TAB_CIERRE}>
            {t("accountantClientDetail.tabs.cierre", "Cierre fiscal")}
          </TabsTrigger>
        </TabsList>

        <TabsContent value={TAB_RESUMEN} className="mt-0 space-y-4">

      {/* Actionable health alerts FIRST — one yellow box per active issue.
          Above the subscription card on purpose: these are the things the
          accountant has to act on, and a missing 206/019 declaration sitting
          UNDER a green "Al día" subscription card read as nothing to do. */}
      <AccountantHealthAlerts
        tenantId={tenantId!}
        deductions={health.deductions}
        pendingAck={pendingAck}
        fiscalBenefitPending={perfilNeedsAttention}
        rejections={rejections}
        onGoToObligations={() =>
          document
            .getElementById("proximos-vencimientos")
            ?.scrollIntoView({ behavior: "smooth", block: "start" })
        }
        onGoToProfile={() => setTab(TAB_PERFIL)}
      />


      {/* Suscripción / Facturación — subscription health + generate a payment
          link the client can use to activate / reactivate their plan. When
          there's a payment issue the card takes a prominent red/amber alert
          treatment (mirrors the tenant dashboard banner) so it can't be missed. */}
      {(() => {
        const paymentIssue =
          tenant.subscriptionState === "past_due" ||
          tenant.subscriptionState === "lapsed";
        // Resolved from the plan catalogue's own i18n, NOT a hand-map. A
        // hand-map only knew the legacy ids (free/pro/ultra) and defaulted
        // everything else to "Tala", so every tenant on a current id —
        // literalE, semilla, tala, ombu, ceibo — displayed as the cheapest
        // tier. Showing an accountant the wrong plan for a client is worse
        // than showing none, so an unknown id renders nothing.
        const planNameKey = `settings.plans.${tenant.planId}.name`;
        const planName =
          tenant.planId && tenant.planId !== "free"
            ? t(planNameKey, { defaultValue: "" }) || null
            : null;
        const cadence =
          tenant.billingInterval === "annual"
            ? t("settings.plans.annual", "Anual")
            : tenant.billingInterval === "monthly"
              ? t("settings.plans.monthly", "Mensual")
              : null;
        const planSummary = [planName, cadence].filter(Boolean).join(" · ");
        return (
          <Card
            className={cn(
              paymentIssue &&
                "border-2 border-[rgba(201,94,62,0.55)] bg-[rgba(201,94,62,0.08)] dark:bg-[rgba(201,94,62,0.16)]",
            )}
          >
            <CardHeader className="flex flex-row items-start justify-between gap-3 pb-3">
              <div>
                <CardTitle className="flex items-center gap-2 text-base">
                  {paymentIssue ? (
                    <AlertTriangle
                      className="h-4 w-4 text-[var(--fx-warn-text)] dark:text-[#E89978]"
                      aria-hidden
                    />
                  ) : (
                    <CreditCard
                      className="h-4 w-4 text-muted-foreground"
                      aria-hidden
                    />
                  )}
                  {t("accountantClientDetail.subscription.title", "Suscripción")}
                </CardTitle>
                <CardDescription>
                  {/* The description followed the button: it promised a
                      payment link on every client, including ones with
                      nothing to pay. */}
                  {paymentIssue
                    ? t(
                        "accountantClientDetail.subscription.description",
                        "Generá un link de pago para que el cliente active o actualice su suscripción.",
                      )
                    : tenant.subscriptionState === "none"
                      ? t(
                          "accountantClientDetail.subscription.descriptionNone",
                          "Este cliente todavía no tiene una suscripción paga.",
                        )
                      : t(
                          "accountantClientDetail.subscription.descriptionActive",
                          "Estado de la suscripción del cliente.",
                        )}
                </CardDescription>
              </div>
              {tenant.subscriptionState === "lapsed" ? (
                <StatusBadge variant="danger">
                  {t(
                    "accountantClientDetail.subscription.lapsed",
                    "Suscripción vencida",
                  )}
                </StatusBadge>
              ) : tenant.subscriptionState === "past_due" ? (
                <StatusBadge variant="warning">
                  {t(
                    "accountantClientDetail.subscription.pastDue",
                    "Pago pendiente",
                  )}
                </StatusBadge>
              ) : tenant.subscriptionState === "active" ? (
                <StatusBadge variant="success">
                  {t("accountantClientDetail.subscription.active", "Al día")}
                </StatusBadge>
              ) : null}
            </CardHeader>
            <CardContent>
              {paymentIssue ? (
                <p className="mb-3 text-sm font-medium text-[var(--fx-warn-text)] dark:text-[#E89978]">
                  {tenant.subscriptionState === "lapsed"
                    ? t(
                        "accountantClientDetail.subscription.lapsedNote",
                        "La suscripción venció por un problema de pago. Generá un link para que el cliente la reactive.",
                      )
                    : t(
                        "accountantClientDetail.subscription.pastDueNote",
                        "Hubo un problema con el último cobro. Generá un link para que el cliente actualice su forma de pago.",
                      )}
                </p>
              ) : null}
              {/* A payment link is a REMEDY, not the default action. Offering
                  it against a healthy subscription reads as "something is
                  wrong here" on a client where nothing is. It stays for a
                  payment issue, and for 'none' — a client with no billable
                  subscription genuinely needs a link to start one. An active
                  subscription gets the plan it is on and a way to change it. */}
              {paymentIssue || tenant.subscriptionState === "none" ? (
                <Button
                  variant={paymentIssue ? undefined : "outline"}
                  className={cn(
                    paymentIssue &&
                      "bg-[var(--fx-warn)] text-white hover:bg-[var(--fx-warn)] hover:opacity-90",
                  )}
                  onClick={() => setShowPaymentLink(true)}
                >
                  <CreditCard className="mr-2 h-4 w-4" />
                  {t(
                    "accountantClientDetail.subscription.generateLink",
                    "Generar link de pago",
                  )}
                </Button>
              ) : (
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="text-sm text-[var(--fx-ink-700)] dark:text-[var(--dk-text-soft)]">
                    {t(
                      "accountantClientDetail.subscription.activeNote",
                      "La suscripción está activa y al día. No hay nada que hacer.",
                    )}
                    {planSummary ? (
                      <span className="ml-1 font-semibold text-[var(--fx-ink-900)] dark:text-[var(--dk-text)]">
                        {planSummary}
                      </span>
                    ) : null}
                  </div>
                  {/* Same dialog: it picks a plan and mints a checkout link,
                      which IS how a plan change happens. Only the framing
                      differs, because the accountant's intent differs. */}
                  <Button variant="outline" onClick={() => setShowPaymentLink(true)}>
                    <CreditCard className="mr-2 h-4 w-4" />
                    {t(
                      "accountantClientDetail.subscription.changePlan",
                      "Cambiar plan",
                    )}
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        );
      })()}

      {/* Fiscal salud breakdown */}
      <Card>
        <CardHeader className="flex flex-row items-start justify-between gap-3 pb-3">
          <div>
            <CardTitle className="flex items-center gap-2 text-base">
              <Heart className="h-4 w-4 text-muted-foreground" aria-hidden />
              {t(
                "accountantClientDetail.healthCard.title",
                "Salud fiscal",
              )}
            </CardTitle>
            <CardDescription>
              {t(
                "accountantClientDetail.healthCard.description",
                "Composición del puntaje del cliente.",
              )}
            </CardDescription>
          </div>
          <div className="text-right">
            <div className="text-3xl font-semibold tabular-nums">
              {health.score}
              <span className="ml-1 text-sm font-normal text-muted-foreground">
                / 100
              </span>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">
            {hasHealthIssues
              ? t(
                  "accountantClientDetail.healthCard.hasIssues",
                  "Revisá los avisos para mejorar el puntaje del cliente.",
                )
              : t(
                  "accountantClientDetail.healthCard.allClear",
                  "Cliente al día — sin deducciones activas.",
                )}
          </p>
        </CardContent>
      </Card>

      {/* Period summary — sales / purchases / IVA (green hero card) */}
      <div className="relative overflow-hidden rounded-[18px] bg-[var(--fx-ink-900)] px-5 py-5 text-[var(--fx-paper)] shadow-[0_18px_44px_-28px_rgba(22,21,20,0.30)] sm:px-7 sm:py-6">
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0"
          style={{
            background:
              "radial-gradient(55% 80% at 100% 0%, rgba(111,165,133,0.22), transparent 60%), radial-gradient(35% 60% at 0% 100%, rgba(111,165,133,0.10), transparent 60%)",
          }}
        />
        <div className="relative">
          <div
            className="flex flex-wrap items-center gap-2 text-[10px] font-bold uppercase text-[var(--fx-accent-300)]"
            style={{ fontFamily: "var(--fx-ff-mono)", letterSpacing: "0.14em" }}
          >
            <span
              aria-hidden
              className="inline-block h-px w-3 bg-[var(--fx-accent-300)]"
            />
            <span>
              {t(
                "accountantClientDetail.periodSummary.eyebrow",
                "Resumen del período",
              )}
            </span>
            <span aria-hidden className="text-[var(--fx-accent-300)]/60">
              ·
            </span>
            <span
              className="font-medium normal-case text-[var(--fx-paper)]/55"
              style={{ letterSpacing: "0.02em" }}
            >
              {formatMonth(ivaCurrent.monthKey)} ·{" "}
              {formatRegimeLabel(ivaCurrent.ivaRegime)}
            </span>
          </div>

          <div className="mt-4 flex flex-col">
            <span
              className="text-[10px] font-semibold uppercase text-[var(--fx-accent-300)]"
              style={{
                fontFamily: "var(--fx-ff-mono)",
                letterSpacing: "0.16em",
              }}
            >
              {t("accountantClientDetail.periodSummary.ventas", "Ventas")}
            </span>
            <span
              className="mt-1 text-4xl font-medium leading-none tracking-tight tabular-nums text-[var(--fx-paper)] sm:text-[52px]"
              style={{ fontFamily: "var(--fx-ff-mono)" }}
            >
              $U {formatDashboardCurrency(ivaCurrent.ventasUyu)}
            </span>
            <span
              className="mt-2 text-[10px] font-semibold uppercase text-[var(--fx-paper)]/45"
              style={{
                fontFamily: "var(--fx-ff-mono)",
                letterSpacing: "0.16em",
              }}
            >
              UYU
            </span>
          </div>

          <div className="my-5 h-px bg-[var(--fx-paper)]/12" />

          <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
            <HeroStat
              label={t(
                "accountantClientDetail.periodSummary.compras",
                "Compras",
              )}
              value={ivaCurrent.comprasUyu}
            />
            <HeroStat
              label={t("accountantClientDetail.ivaCurrent.debited", "IVA débito")}
              value={ivaCurrent.ivaDebitedUyu}
            />
            <HeroStat
              label={t(
                "accountantClientDetail.ivaCurrent.credited",
                "IVA crédito",
              )}
              value={ivaCurrent.ivaCreditedUyu}
            />
            <HeroStat
              label={t("accountantClientDetail.ivaCurrent.toPay", "A pagar (neto)")}
              value={ivaCurrent.ivaAPagarNetoUyu}
              highlight
            />
          </div>

          {ivaCurrent.ivaCreditBalanceUyu > 0 ? (
            <p className="mt-4 text-[11px] text-[var(--fx-paper)]/50">
              {t(
                "accountantClientDetail.ivaCurrent.creditBalanceHint",
                "Saldo a favor acumulado de períodos anteriores: {{amount}}",
                { amount: formatUyu(ivaCurrent.ivaCreditBalanceUyu, locale) },
              )}
            </p>
          ) : null}
        </div>
      </div>

      {/* Boleto de pago for the current period */}
      <BoletoPagoCard
        tenantId={tenantId!}
        obligationId={ivaCurrent.obligationId}
        aPagarUyu={ivaCurrent.ivaAPagarNetoUyu}
        boleto={ivaCurrent.boleto}
        onGenerated={async () => {
          await detailQuery.refetch();
        }}
      />

      {/* Upcoming obligations */}
      <Card id="proximos-vencimientos">
        <CardHeader className="pb-3">
          <CardTitle className="text-base">
            {t(
              "accountantClientDetail.obligations.title",
              "Próximos vencimientos",
            )}
          </CardTitle>
          <CardDescription>
            {t(
              "accountantClientDetail.obligations.description",
              "Obligaciones DGI y BPS pendientes.",
            )}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {upcomingObligations.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              {t(
                "accountantClientDetail.obligations.empty",
                "Sin obligaciones pendientes.",
              )}
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>
                    {t(
                      "accountantClientDetail.obligations.col.tax",
                      "Impuesto",
                    )}
                  </TableHead>
                  <TableHead>
                    {t(
                      "accountantClientDetail.obligations.col.period",
                      "Período",
                    )}
                  </TableHead>
                  <TableHead>
                    {t(
                      "accountantClientDetail.obligations.col.dueDate",
                      "Vencimiento",
                    )}
                  </TableHead>
                  <TableHead className="text-right">
                    {t(
                      "accountantClientDetail.obligations.col.amount",
                      "Monto",
                    )}
                  </TableHead>
                  <TableHead>
                    {t(
                      "accountantClientDetail.obligations.col.status",
                      "Estado",
                    )}
                  </TableHead>
                  <TableHead className="text-right">
                    <span className="sr-only">
                      {t(
                        "accountantClientDetail.obligations.col.action",
                        "Acción",
                      )}
                    </span>
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {upcomingObligations.map((o) => {
                  const overdue = o.status === "overdue";
                  const dueSoon =
                    o.status === "dueSoon" || o.status === "due_today";
                  return (
                    <TableRow key={o.id}>
                      <TableCell className="font-medium">
                        {o.taxType}{" "}
                        <span className="text-xs text-muted-foreground">
                          ({o.authority})
                        </span>
                      </TableCell>
                      <TableCell>{o.period}</TableCell>
                      <TableCell className="tabular-nums">
                        {o.dueDate
                          ? formatDisplayDate(o.dueDate, {
                              locale,
                              useCalendarDate: true,
                            })
                          : "—"}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {o.amountOwedUyu != null
                          ? formatUyu(o.amountOwedUyu, locale)
                          : "—"}
                      </TableCell>
                      <TableCell>
                        {overdue ? (
                          <StatusBadge
                            variant="danger"
                            className="inline-flex items-center gap-1"
                          >
                            <AlertTriangle
                              className="h-3 w-3"
                              aria-hidden
                            />
                            {t(
                              "accountantClientDetail.obligations.status.overdue",
                              "Vencido",
                            )}
                          </StatusBadge>
                        ) : dueSoon ? (
                          <StatusBadge
                            variant="warning"
                            className="inline-flex"
                          >
                            {t(
                              "accountantClientDetail.obligations.status.dueSoon",
                              "Próximo",
                            )}
                          </StatusBadge>
                        ) : (
                          <span className="text-sm text-muted-foreground">
                            {t(
                              "accountantClientDetail.obligations.status.upcoming",
                              "Pendiente",
                            )}
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <AccountantObligationPaidControl
                          tenantId={tenantId!}
                          obligationId={o.id}
                          taxType={o.taxType}
                          period={o.period}
                          suggestedAmountUyu={o.amountOwedUyu}
                        />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <AccountantBajaCard
        tenantId={tenantId!}
        bajaAt={tenant.bajaAt}
        bajaReason={tenant.bajaReason}
      />
        </TabsContent>

        <TabsContent value={TAB_IVA} className="mt-0 space-y-4">

      {/* IVA history table */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">
            {t(
              "accountantClientDetail.ivaHistory.title",
              "IVA — últimos 12 meses",
            )}
          </CardTitle>
          <CardDescription>
            {t(
              "accountantClientDetail.ivaHistory.description",
              "Débito, crédito, a pagar y pago registrado por período.",
            )}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {ivaHistoryQuery.isLoading ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              {t("accountantClientDetail.ivaHistory.loading", "Cargando…")}
            </div>
          ) : (ivaHistoryQuery.data?.length ?? 0) === 0 ? (
            <p className="text-sm text-muted-foreground">
              {t(
                "accountantClientDetail.ivaHistory.empty",
                "Sin períodos de IVA registrados.",
              )}
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>
                    {t(
                      "accountantClientDetail.ivaHistory.col.period",
                      "Período",
                    )}
                  </TableHead>
                  <TableHead className="text-right">
                    {t(
                      "accountantClientDetail.ivaHistory.col.debited",
                      "Débito",
                    )}
                  </TableHead>
                  <TableHead className="text-right">
                    {t(
                      "accountantClientDetail.ivaHistory.col.credited",
                      "Crédito",
                    )}
                  </TableHead>
                  <TableHead className="text-right">
                    {t(
                      "accountantClientDetail.ivaHistory.col.toPay",
                      "A pagar",
                    )}
                  </TableHead>
                  <TableHead className="text-right">
                    {t(
                      "accountantClientDetail.ivaHistory.col.paid",
                      "Pagado",
                    )}
                  </TableHead>
                  <TableHead className="w-8" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {(ivaHistoryQuery.data ?? [])
                  .slice()
                  .reverse()
                  .map((item) => {
                    const isCurrent = item.period === currentYm;
                    return (
                    <TableRow
                      key={item.period}
                      onRowClick={() =>
                        navigate(
                          `/accountant/client/${tenantId}/iva/${item.period}`,
                        )
                      }
                      className={cn(
                        isCurrent &&
                          "bg-[var(--fx-accent-100)]/40 hover:bg-[var(--fx-accent-100)]/60",
                      )}
                    >
                      <TableCell className="font-medium">
                        <span className="inline-flex items-center gap-2">
                          <span className={cn(isCurrent && "font-semibold")}>
                            {formatMonth(item.period)}
                          </span>
                          {isCurrent ? (
                            <span className="rounded-full bg-[var(--fx-accent-300)]/20 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-[var(--fx-accent-500)]">
                              {t(
                                "accountantClientDetail.ivaHistory.currentMonth",
                                "Mes en curso",
                              )}
                            </span>
                          ) : null}
                        </span>
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {formatUyu(item.ivaDebitedUyu, locale)}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {formatUyu(item.ivaCreditedUyu, locale)}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {formatUyu(item.ivaAPagarUyu, locale)}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {item.ivaActuallyPaidUyu != null ? (
                          <span className="inline-flex items-center gap-1 text-emerald-700 dark:text-emerald-400">
                            <Check className="h-3 w-3" aria-hidden />
                            {formatUyu(item.ivaActuallyPaidUyu, locale)}
                          </span>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        <ChevronRight className="h-4 w-4" aria-hidden />
                      </TableCell>
                    </TableRow>
                    );
                  })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Subdiario de Ventas — monthly sales summary, click a row for detail */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">
            {t("accountantClientDetail.subdiario.title", "Subdiario de Ventas")}
          </CardTitle>
          <CardDescription>
            {t(
              "accountantClientDetail.subdiario.description",
              "Ventas por mes (últimos 12 meses). Tocá un mes para ver el detalle por comprobante. Montos en UYU.",
            )}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {subdiarioQuery.isLoading ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              {t("accountantClientDetail.subdiario.loading", "Cargando…")}
            </div>
          ) : (subdiarioQuery.data?.rows.length ?? 0) === 0 ? (
            <p className="text-sm text-muted-foreground">
              {t(
                "accountantClientDetail.subdiario.empty",
                "Sin ventas confirmadas en los últimos 12 meses.",
              )}
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>
                    {t("accountantClientDetail.subdiario.col.period", "Mes")}
                  </TableHead>
                  <TableHead className="text-right">
                    {t("accountantClientDetail.subdiario.col.count", "CFE")}
                  </TableHead>
                  <TableHead className="text-right">
                    {t("accountantClientDetail.subdiario.col.neto", "Neto")}
                  </TableHead>
                  <TableHead className="text-right">
                    {t("accountantClientDetail.subdiario.col.iva", "IVA")}
                  </TableHead>
                  <TableHead className="text-right">
                    {t("accountantClientDetail.subdiario.col.total", "Total")}
                  </TableHead>
                  <TableHead className="w-8" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {(subdiarioQuery.data?.rows ?? []).map((row) => (
                  <TableRow
                    key={row.period}
                    onRowClick={() =>
                      navigate(
                        `/accountant/client/${tenantId}/subdiario/${row.period}`,
                      )
                    }
                  >
                    <TableCell className="font-medium">
                      {formatMonth(row.period)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {row.cfeCount}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {formatUyu(row.netoUyu, locale)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {formatUyu(row.ivaUyu, locale)}
                    </TableCell>
                    <TableCell className="text-right font-medium tabular-nums">
                      {formatUyu(row.totalUyu, locale)}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      <ChevronRight className="h-4 w-4" aria-hidden />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

        </TabsContent>

        <TabsContent value={TAB_PERFIL} className="mt-0 space-y-4">
          {/* Above the profile card on purpose: a client with no declaration is
              owed an action, and this is the only surface that reaches tenants
              who finished onboarding before the declaration existed. */}
          <AccountantFiscalBenefitCard tenantId={tenantId!} />
          <SettingsFiscalIdentityCard
            scopeKey={tenantId!}
            load={() => getAccountantTenantFiscalIdentity(tenantId!)}
            loadFromDgi={() => getAccountantTenantFiscalIdentityFromDgi(tenantId!)}
            save={(updates) =>
              updateAccountantTenantFiscalIdentity(tenantId!, updates)
            }
          />
          <AccountantFiscalProfileCard tenantId={tenantId!} />
        </TabsContent>

        <TabsContent value={TAB_CIERRE} className="mt-0 space-y-4">
          {activeTab === TAB_CIERRE ? (
            <CierreFiscalTab tenantId={tenantId!} />
          ) : null}
        </TabsContent>
      </Tabs>

      <GeneratePaymentLinkDialog
        tenant={
          showPaymentLink
            ? { id: tenant.id, name: tenant.name, rut: tenant.rut }
            : null
        }
        onClose={() => setShowPaymentLink(false)}
        forcePickerOnOpen
      />
    </div>
  );
}

/** One UYU figure inside the period-summary hero card (mono, brand accent label). */
function HeroStat({
  label,
  value,
  highlight,
}: {
  label: string;
  value: number;
  highlight?: boolean;
}): JSX.Element {
  return (
    <div className="flex flex-col gap-1">
      <span
        className="text-[9.5px] font-bold uppercase text-[var(--fx-accent-300)]"
        style={{ fontFamily: "var(--fx-ff-mono)", letterSpacing: "0.14em" }}
      >
        {label}
      </span>
      <span
        className={cn(
          "text-base font-medium leading-none tabular-nums sm:text-lg",
          highlight ? "text-[var(--fx-paper)]" : "text-[var(--fx-paper)]/80",
        )}
        style={{ fontFamily: "var(--fx-ff-mono)" }}
      >
        $U {formatDashboardCurrency(value)}
      </span>
    </div>
  );
}
